import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthService } from '../services/authService';
import { ApiService } from '../services/apiService';
import EmailEntryLayout from './EmailEntryLayout';
import EmailValidation from './EmailValidation';
import '../pages/EmailValidation.css';
import './EmailAuthProtectedRoute.css';

interface EmailAuthProtectedRouteProps {
  children: React.ReactNode;
  fallbackComponent?: React.ReactNode;
  pageTitle?: string;
  pageDescription?: string;
}

export const EmailAuthProtectedRoute: React.FC<EmailAuthProtectedRouteProps> = ({ 
  children, 
  fallbackComponent,
  pageTitle = "Email Verification Required",
  pageDescription = "Please verify your email address to access this page."
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isValidatingAuth, setIsValidatingAuth] = useState(true);
  const [showEmailValidation, setShowEmailValidation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = () => {
      if (AuthService.isAuthenticated()) {
        setIsValidatingAuth(false);
        setShowEmailValidation(false);
      } else {
        setIsValidatingAuth(false);
        setShowEmailValidation(true);
      }
    };

    checkAuth();
  }, []);

  const handleEmailValidationSuccess = async (email: string) => {
    try {
      setIsLoading(true);
      setError('');
      
      // Get cardId from URL params
      const searchParams = new URLSearchParams(location.search);
      const cardId = searchParams.get('cardId');
      
      if (!cardId) {
        setError('Card ID is required for email validation');
        return;
      }

      // First, handle the user (create or get existing)
      await ApiService.handleEmailUser(email);
      
      // Then send validation email (not verification email)
      const response = await ApiService.sendValidationEmail(email, cardId, location.pathname + location.search);

      if (response.success) {
        // Redirect to email-sent page
        navigate('/email-sent', { 
          state: { 
            email: email, 
            cardId: cardId,
            returnTo: location.pathname + location.search,
            isValidationEmail: true // Flag to indicate this is validation, not registration
          } 
        });
      } else {
        setError('Failed to send validation email. Please try again.');
      }
    } catch (error: any) {
      console.error('❌ EmailAuthProtectedRoute: Error sending validation email:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailValidationError = (errorMessage: string) => {
    console.error('❌ EmailAuthProtectedRoute: Email validation error:', errorMessage);
    setError(errorMessage);
  };

  // Show loading while checking authentication
  if (isValidatingAuth) {
    return (
      <div className="email-auth-loading">
        <div className="loading-spinner"></div>
        <p>Checking authentication...</p>
      </div>
    );
  }

  // Show email validation if not authenticated (same look as EmailValidation page)
  const useCustomHeadline = pageTitle !== 'Email Verification Required';
  if (showEmailValidation) {
    return (
      <EmailEntryLayout
        {...(useCustomHeadline && { headline: pageTitle, instruction: pageDescription })}
      >
        {error && (
          <div className="error-message ev-card-content">
            {error}
          </div>
        )}
        <EmailValidation
          onValidationSuccess={handleEmailValidationSuccess}
          onValidationError={handleEmailValidationError}
          isLoading={isLoading}
          showDisclaimer={true}
          disclaimerContent={
            <>
              By clicking Send Verification Email, I agree to the{' '}
              <a href="https://www.mycryptocard.io/terms" target="_blank" rel="noopener noreferrer" className="ev-link">Terms and Services</a>
              {' '}and{' '}
              <a href="https://www.mycryptocard.io/privacy" target="_blank" rel="noopener noreferrer" className="ev-link">Privacy Policy</a>.
              By entering my email address, I agree to receive transactional emails.
            </>
          }
        />
      </EmailEntryLayout>
    );
  }

  // Show protected content if authenticated
  return <>{children}</>;
};

export default EmailAuthProtectedRoute;